import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  PencilSquareIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  HomeModernIcon,
  ArchiveBoxIcon,
  TrashIcon,
  UsersIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/Badge";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { cabinGroupsApi } from "../../api/cabinGroups";
import { cabinsApi } from "../../api/cabins";
import { CabinDetailModal } from "../../components/cabins/CabinDetailModal";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import type { Division, Cabin } from "../../types";

const CURRENT_YEAR = new Date().getFullYear();

const GRADE_NAMES: Record<number, string> = {
  [-3]: "Infant", [-2]: "Toddler", [-1]: "Nursery",
  [0]: "Pre-K", [1]: "K", [2]: "1st", [3]: "2nd", [4]: "3rd",
  [5]: "4th", [6]: "5th", [7]: "6th", [8]: "7th", [9]: "8th",
  [10]: "9th", [11]: "10th", [12]: "11th", [13]: "12th", [14]: "12th+",
};

function gradeRangeLabel(start?: number | null, end?: number | null): string {
  if (start == null && end == null) return "";
  const s = start != null ? (GRADE_NAMES[start] ?? String(start)) : "?";
  const e = end != null ? (GRADE_NAMES[end] ?? String(end)) : "?";
  return start === end ? s : `${s}–${e}`;
}

function genderLabel(genderId?: number | null): string | null {
  if (genderId == null) return null;
  if (genderId === 0) return "Female";
  if (genderId === 1) return "Male";
  if (genderId === 2) return "All";
  return null;
}

function divisionRangeLabel(d: Division): string {
  const fromGrades = gradeRangeLabel(d.startGradeId ?? null, d.endGradeId ?? null);
  return fromGrades || d.ageRange || "";
}

// ─── Division form ────────────────────────────────────────────────────────────

const divisionSchema = z.object({
  name: z.string().min(1, "Name is required"),
  ageRange: z.string().min(1, "Grade range is required"),
  description: z.string().optional(),
});

type DivisionFormData = z.infer<typeof divisionSchema>;

// ─── Cabin form ───────────────────────────────────────────────────────────────

const cabinSchema = z.object({
  name: z.string().min(1, "Name is required"),
  divisionId: z.coerce.number().optional().transform((v) => (v && v > 0 ? v : undefined)),
});

type CabinFormData = z.infer<typeof cabinSchema>;

export default function ManageCabins() {
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [cabins, setCabins] = useState<Cabin[]>([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Bunk area mapping state
  const [bunkAreaMappings, setBunkAreaMappings] = useState<Record<number, number>>({});
  const [applyingMappings, setApplyingMappings] = useState(false);

  // Division modal state
  const [divisionModalOpen, setDivisionModalOpen] = useState(false);
  const [editingDivision, setEditingDivision] = useState<Division | null>(null);
  const [savingDivision, setSavingDivision] = useState(false);
  const [archivingDivision, setArchivingDivision] = useState(false);
  const [deleteDivisionConfirm, setDeleteDivisionConfirm] = useState(false);
  const [deletingDivision, setDeletingDivision] = useState(false);
  const [divisionToDelete, setDivisionToDelete] = useState<{ id: number; name: string; fromCampMinder: boolean } | null>(null);

  // Cabin modal state
  const [cabinModalOpen, setCabinModalOpen] = useState(false);
  const [editingCabin, setEditingCabin] = useState<Cabin | null>(null);
  const [savingCabin, setSavingCabin] = useState(false);
  const [archivingCabin, setArchivingCabin] = useState(false);
  const [deleteCabinConfirm, setDeleteCabinConfirm] = useState(false);
  const [deletingCabin, setDeletingCabin] = useState(false);
  const [cabinToDelete, setCabinToDelete] = useState<{ id: number; name: string; fromCampMinder: boolean } | null>(null);

  // Cabin assignment modal state
  const [cabinDetailOpen, setCabinDetailOpen] = useState(false);
  const [selectedCabin, setSelectedCabin] = useState<Cabin | null>(null);

  const deepLinkHandled = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      cabinGroupsApi.listDivisions(showArchived),
      cabinsApi.list({ includeArchived: showArchived }),
    ])
      .then(([divs, cabinList]) => {
        setDivisions(divs);
        setCabins(cabinList);
      })
      .catch(() => toast.error("Failed to load cabins"))
      .finally(() => setLoading(false));
  }, [showArchived]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Derived groupings ────────────────────────────────────────────────────

  // Cabins grouped by their assigned division
  const cabinsByDivision = useMemo(() => {
    const map = new Map<number, Cabin[]>();
    for (const cabin of cabins) {
      if (cabin.divisionId) {
        const list = map.get(cabin.divisionId) ?? [];
        map.set(cabin.divisionId, [...list, cabin]);
      }
    }
    return map;
  }, [cabins]);

  // Cabins that have a CampMinder bunk area but no division yet (need mapping)
  const unmappedByArea = useMemo(() => {
    const map = new Map<number, Cabin[]>();
    for (const cabin of cabins) {
      if (!cabin.divisionId && cabin.campMinderBunkAreaId && !cabin.archivedAt) {
        const list = map.get(cabin.campMinderBunkAreaId) ?? [];
        map.set(cabin.campMinderBunkAreaId, [...list, cabin]);
      }
    }
    return map;
  }, [cabins]);

  // Cabins with neither division nor bunk area
  const fullyUnassigned = useMemo(
    () => cabins.filter((c) => !c.divisionId && !c.campMinderBunkAreaId && !c.archivedAt),
    [cabins]
  );

  // ─── Reorder helpers ──────────────────────────────────────────────────────

  const moveDivision = async (index: number, direction: "up" | "down") => {
    const activeDivisions = divisions.filter((d) => !d.archivedAt);
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= activeDivisions.length) return;

    const next = [...divisions];
    const activeIndices = next.reduce<number[]>((acc, d, i) => {
      if (!d.archivedAt) acc.push(i);
      return acc;
    }, []);
    const realIdx = activeIndices[index];
    const realSwapIdx = activeIndices[swapIndex];
    [next[realIdx], next[realSwapIdx]] = [next[realSwapIdx], next[realIdx]];
    setDivisions(next);

    setReordering(true);
    try {
      await cabinGroupsApi.reorderDivisions(
        next.filter((d) => !d.archivedAt).map((d) => d.id)
      );
    } catch {
      fetchData();
      toast.error("Failed to reorder divisions");
    } finally {
      setReordering(false);
    }
  };

  // ─── Division form ────────────────────────────────────────────────────────

  const divisionForm = useForm<DivisionFormData>({
    resolver: zodResolver(divisionSchema),
  });

  const openEditDivision = (division: Division) => {
    setEditingDivision(division);
    divisionForm.reset({
      name: division.name,
      ageRange: division.ageRange ?? "",
      description: division.description ?? "",
    });
    setDivisionModalOpen(true);
  };

  const onSubmitDivision = async (data: DivisionFormData) => {
    setSavingDivision(true);
    try {
      if (editingDivision) {
        await cabinGroupsApi.updateDivision(editingDivision.id, data);
        toast.success("Division updated!");
      } else {
        await cabinGroupsApi.createDivision(data as Omit<Division, "id" | "cabinGroups" | "sortOrder">);
        toast.success("Division created!");
      }
      setDivisionModalOpen(false);
      setEditingDivision(null);
      fetchData();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save division");
    } finally {
      setSavingDivision(false);
    }
  };

  const handleArchiveDivision = async () => {
    if (!editingDivision) return;
    setArchivingDivision(true);
    try {
      await cabinGroupsApi.archiveDivision(editingDivision.id);
      toast.success(editingDivision.archivedAt ? "Division restored!" : "Division archived!");
      setDivisionModalOpen(false);
      setEditingDivision(null);
      fetchData();
    } catch {
      toast.error("Failed to archive division");
    } finally {
      setArchivingDivision(false);
    }
  };

  const handleDeleteDivision = async () => {
    if (!divisionToDelete) return;
    setDeletingDivision(true);
    try {
      await cabinGroupsApi.deleteDivision(divisionToDelete.id);
      toast.success("Division permanently deleted!");
      setDeleteDivisionConfirm(false);
      setDivisionToDelete(null);
      fetchData();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || "Failed to delete division");
    } finally {
      setDeletingDivision(false);
    }
  };

  // ─── Cabin form ───────────────────────────────────────────────────────────

  const cabinForm = useForm<CabinFormData>({
    resolver: zodResolver(cabinSchema),
  });

  const openEditCabin = (cabin: Cabin) => {
    setEditingCabin(cabin);
    cabinForm.reset({ name: cabin.name, divisionId: cabin.divisionId ?? 0 });
    setCabinModalOpen(true);
  };

  // Deep-link: open edit modal when ?editCabin=<id> or ?editDivision=<id> is in URL
  useEffect(() => {
    if (loading || deepLinkHandled.current) return;
    const editCabinId = searchParams.get("editCabin");
    const editDivisionId = searchParams.get("editDivision");
    if (!editCabinId && !editDivisionId) return;
    deepLinkHandled.current = true;
    setSearchParams((p) => { p.delete("editCabin"); p.delete("editDivision"); return p; }, { replace: true });
    if (editCabinId) {
      const cabin = cabins.find((c) => c.id === Number(editCabinId));
      if (cabin && cabin.campMinderBunkId == null) openEditCabin(cabin);
    } else if (editDivisionId) {
      const division = divisions.find((d) => d.id === Number(editDivisionId));
      if (division && division.campMinderDivisionId == null) openEditDivision(division);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, cabins, divisions]);

  const onSubmitCabin = async (data: CabinFormData) => {
    setSavingCabin(true);
    try {
      if (editingCabin) {
        await cabinsApi.update(editingCabin.id, {
          name: data.name,
          divisionId: data.divisionId ?? null,
        });
        toast.success("Cabin updated!");
      } else {
        await cabinsApi.create({
          name: data.name,
          divisionId: data.divisionId ?? null,
        });
        toast.success("Cabin created!");
      }
      setCabinModalOpen(false);
      setEditingCabin(null);
      fetchData();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save cabin");
    } finally {
      setSavingCabin(false);
    }
  };

  const handleArchiveCabin = async () => {
    if (!editingCabin) return;
    setArchivingCabin(true);
    try {
      await cabinsApi.archive(editingCabin.id);
      toast.success(editingCabin.archivedAt ? "Cabin restored!" : "Cabin archived!");
      setCabinModalOpen(false);
      setEditingCabin(null);
      fetchData();
    } catch {
      toast.error("Failed to archive cabin");
    } finally {
      setArchivingCabin(false);
    }
  };

  const handleDeleteCabin = async () => {
    if (!cabinToDelete) return;
    setDeletingCabin(true);
    try {
      await cabinsApi.delete(cabinToDelete.id);
      toast.success("Cabin permanently deleted!");
      setDeleteCabinConfirm(false);
      setCabinToDelete(null);
      fetchData();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || "Failed to delete cabin");
    } finally {
      setDeletingCabin(false);
    }
  };

  // ─── Bunk area mapping ────────────────────────────────────────────────────

  const handleApplyMappings = async () => {
    const entries = Object.entries(bunkAreaMappings).filter(([, divId]) => divId > 0);
    if (entries.length === 0) {
      toast("Select a division for at least one bunk area.");
      return;
    }
    setApplyingMappings(true);
    try {
      await Promise.all(
        entries.map(([areaId, divId]) => cabinGroupsApi.mapBunkArea(divId, Number(areaId)))
      );
      toast.success(`${entries.length} bunk area${entries.length !== 1 ? "s" : ""} mapped!`);
      setBunkAreaMappings({});
      fetchData();
    } catch {
      toast.error("Failed to apply mappings");
    } finally {
      setApplyingMappings(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const activeDivisions = divisions.filter((d) => !d.archivedAt);
  const archivedDivisions = divisions.filter((d) => !!d.archivedAt);
  const activeCabinCount = cabins.filter((c) => !c.archivedAt).length;
  const assignedCabinCount = cabins.filter((c) => !c.archivedAt && c.divisionId).length;

  return (
    <div>
      <PageHeader
        title="Cabins & Divisions"
      />

      <div className="p-6 lg:p-8">
        {/* Show archived toggle */}
        <div className="flex items-center gap-2 mb-4">
          <label className="flex items-center gap-2 text-sm text-stone-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded border-stone-300 text-forest-600 focus:ring-forest-500"
            />
            Show archived
          </label>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="camp-card p-5 animate-pulse space-y-3">
                <div className="h-5 bg-stone-200 rounded w-1/4" />
                <div className="h-3 bg-stone-100 rounded w-1/2" />
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div className="h-12 bg-stone-100 rounded" />
                  <div className="h-12 bg-stone-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : divisions.length === 0 ? (
          <div className="text-center py-16">
            <HomeModernIcon className="w-10 h-10 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500 text-sm">
              No divisions yet. Create a division to get started.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary */}
            <p className="text-sm text-stone-400">
              {activeDivisions.length} division{activeDivisions.length !== 1 ? "s" : ""} · {assignedCabinCount}/{activeCabinCount} cabin{activeCabinCount !== 1 ? "s" : ""} assigned
              {showArchived && archivedDivisions.length > 0 && (
                <span> · {archivedDivisions.length} archived</span>
              )}
            </p>

            {/* Bunk area mapping banner */}
            {unmappedByArea.size > 0 && (
              <div className="camp-card p-5 border-l-4 border-amber-400">
                <div className="flex items-start gap-3 mb-4">
                  <LinkIcon className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-stone-800">
                      {Array.from(unmappedByArea.values()).reduce((n, list) => n + list.length, 0)} cabin{Array.from(unmappedByArea.values()).reduce((n, list) => n + list.length, 0) !== 1 ? "s" : ""} imported from CampMinder need division assignment
                    </p>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Map each Bunk Area to one of your divisions below. This only needs to be done once — future syncs will assign cabins automatically.
                    </p>
                  </div>
                </div>
                <div className="space-y-3 ml-8">
                  {Array.from(unmappedByArea.entries()).sort(([a], [b]) => Number(a) - Number(b)).map(([areaId, areaCabins]) => (
                    <div key={areaId} className="flex items-center gap-3">
                      <span className="text-sm text-stone-600 w-52 flex-shrink-0">
                        Bunk Area {areaId} ({areaCabins.length} cabin{areaCabins.length !== 1 ? "s" : ""})
                      </span>
                      <Select
                        options={[
                          { value: 0, label: "Select division…" },
                          ...activeDivisions.map((d) => ({ value: d.id, label: d.name })),
                        ]}
                        value={bunkAreaMappings[areaId] ?? 0}
                        onChange={(e) =>
                          setBunkAreaMappings((prev) => ({
                            ...prev,
                            [areaId]: Number(e.target.value),
                          }))
                        }
                        className="max-w-[200px]"
                      />
                    </div>
                  ))}
                  <div className="pt-1">
                    <Button
                      variant="primary"
                      loading={applyingMappings}
                      onClick={handleApplyMappings}
                    >
                      Apply Mapping
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Active divisions */}
            {activeDivisions.map((division, index) => {
              const divCabins = (cabinsByDivision.get(division.id) ?? []).filter((c) => !c.archivedAt);
              const archivedDivCabins = (cabinsByDivision.get(division.id) ?? []).filter((c) => !!c.archivedAt);
              const isFirst = index === 0;
              const isLast = index === activeDivisions.length - 1;

              return (
                <div key={division.id} className="camp-card overflow-hidden">
                  {/* Division header */}
                  <div className="flex items-center gap-3 px-5 py-4 bg-stone-50 border-b border-stone-200/60">
                    {/* Reorder buttons */}
                    <div className="flex flex-col -my-1">
                      <button
                        onClick={() => moveDivision(index, "up")}
                        disabled={isFirst || reordering}
                        className="p-0.5 rounded text-stone-400 hover:text-forest-600 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
                        title="Move up"
                      >
                        <ChevronUpIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => moveDivision(index, "down")}
                        disabled={isLast || reordering}
                        className="p-0.5 rounded text-stone-400 hover:text-forest-600 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
                        title="Move down"
                      >
                        <ChevronDownIcon className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Position badge */}
                    <span className="w-6 h-6 rounded-full bg-forest-100 text-forest-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {index + 1}
                    </span>

                    {/* Title area */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-stone-900 truncate">
                          {division.name}
                        </h3>
                        {divisionRangeLabel(division) && (
                          <Badge label={divisionRangeLabel(division)} color="stone" />
                        )}
                        {genderLabel(division.genderId) && (
                          <Badge label={genderLabel(division.genderId)!} color="stone" />
                        )}
                        {division.campMinderDivisionId != null && (
                          <span className="text-xs font-medium text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">
                            CampMinder
                          </span>
                        )}
                      </div>
                      {division.description && (
                        <p className="text-xs text-stone-400 mt-0.5 truncate">
                          {division.description}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <span className="text-xs text-stone-400 mr-2">
                        {divCabins.length} cabin{divCabins.length !== 1 ? "s" : ""}
                      </span>
                      {division.campMinderDivisionId == null && (
                        <button
                          onClick={() => openEditDivision(division)}
                          className="p-1.5 rounded-lg hover:bg-stone-200/60 text-stone-400 hover:text-forest-600 transition-colors"
                          title="Edit division"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Cabins in this division */}
                  {divCabins.length === 0 && archivedDivCabins.length === 0 ? (
                    <div className="px-5 py-6 text-center">
                      <p className="text-sm text-stone-400">
                        No cabins in this division yet.
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-stone-100">
                      {divCabins.map((cabin) => (
                        <div
                          key={cabin.id}
                          className="flex items-center justify-between px-5 py-3 hover:bg-stone-50/50 transition-colors"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <HomeModernIcon className="w-4 h-4 text-bark-400 flex-shrink-0" />
                            <span className="font-medium text-stone-800 text-sm truncate">
                              {cabin.name}
                            </span>
                            {cabin.campMinderBunkId != null && (
                              <span className="text-xs font-medium text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded flex-shrink-0">
                                CampMinder
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="text-xs text-stone-400 tabular-nums">
                              {cabin.camperCount ?? 0} campers
                            </span>
                            {cabin.campMinderBunkId == null && (
                              <>
                                <button
                                  onClick={() => { setSelectedCabin(cabin); setCabinDetailOpen(true); }}
                                  className="p-1 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-forest-600 transition-colors"
                                  title="Manage cabin assignments"
                                >
                                  <UsersIcon className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => openEditCabin(cabin)}
                                  className="p-1 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-forest-600 transition-colors"
                                  title="Edit cabin"
                                >
                                  <PencilSquareIcon className="w-3.5 h-3.5" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                      {/* Archived cabins (shown when toggle is on) */}
                      {showArchived && archivedDivCabins.map((cabin) => (
                        <div
                          key={cabin.id}
                          className="flex items-center justify-between px-5 py-3 hover:bg-stone-50/50 transition-colors opacity-60"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <HomeModernIcon className="w-4 h-4 text-stone-300 flex-shrink-0" />
                            <span className="font-medium text-stone-500 text-sm truncate">
                              {cabin.name}
                            </span>
                            <Badge label="Archived" color="stone" />
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            {cabin.campMinderBunkId == null && (
                              <button
                                onClick={() => openEditCabin(cabin)}
                                className="p-1 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-forest-600 transition-colors"
                                title="Edit cabin"
                              >
                                <PencilSquareIcon className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Unassigned cabins (no division, no bunk area) */}
            {fullyUnassigned.length > 0 && (
              <div className="camp-card overflow-hidden">
                <div className="flex items-center gap-3 px-5 py-4 bg-amber-50/60 border-b border-stone-200/60">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-stone-600 truncate">
                        Unassigned
                      </h3>
                      <Badge label="No Division" color="amber" />
                    </div>
                    <p className="text-xs text-stone-400 mt-0.5">
                      Cabins not assigned to any division
                    </p>
                  </div>
                  <span className="text-xs text-stone-400">
                    {fullyUnassigned.length} cabin{fullyUnassigned.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="divide-y divide-stone-100">
                  {fullyUnassigned.map((cabin) => (
                    <div
                      key={cabin.id}
                      className="flex items-center justify-between px-5 py-3 hover:bg-stone-50/50 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <HomeModernIcon className="w-4 h-4 text-amber-400 flex-shrink-0" />
                        <span className="font-medium text-stone-800 text-sm truncate">
                          {cabin.name}
                        </span>
                        {cabin.campMinderBunkId != null && (
                          <span className="text-xs font-medium text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded flex-shrink-0">
                            CampMinder
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {cabin.campMinderBunkId == null && (
                          <>
                            <button
                              onClick={() => { setSelectedCabin(cabin); setCabinDetailOpen(true); }}
                              className="p-1 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-forest-600 transition-colors"
                              title="Manage cabin assignments"
                            >
                              <UsersIcon className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => openEditCabin(cabin)}
                              className="p-1 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-forest-600 transition-colors"
                              title="Edit cabin"
                            >
                              <PencilSquareIcon className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Archived divisions */}
            {showArchived && archivedDivisions.map((division) => {
              const divCabins = cabinsByDivision.get(division.id) ?? [];
              return (
                <div key={division.id} className="camp-card overflow-hidden opacity-60">
                  <div className="flex items-center gap-3 px-5 py-4 bg-stone-50 border-b border-stone-200/60">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-stone-500 truncate">
                          {division.name}
                        </h3>
                        {divisionRangeLabel(division) && (
                          <Badge label={divisionRangeLabel(division)} color="stone" />
                        )}
                        <Badge label="Archived" color="stone" />
                        {division.campMinderDivisionId != null && (
                          <span className="text-xs font-medium text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">
                            CampMinder
                          </span>
                        )}
                      </div>
                      {division.description && (
                        <p className="text-xs text-stone-400 mt-0.5 truncate">
                          {division.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <span className="text-xs text-stone-400 mr-2">
                        {divCabins.length} cabin{divCabins.length !== 1 ? "s" : ""}
                      </span>
                      {division.campMinderDivisionId == null && (
                        <button
                          onClick={() => openEditDivision(division)}
                          className="p-1.5 rounded-lg hover:bg-stone-200/60 text-stone-400 hover:text-forest-600 transition-colors"
                          title="Edit division"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Division Modal */}
      <Modal
        open={divisionModalOpen}
        onClose={() => {
          setDivisionModalOpen(false);
          setEditingDivision(null);
        }}
        title={editingDivision ? "Edit Division" : "Create Division"}
      >
        <form onSubmit={divisionForm.handleSubmit(onSubmitDivision)} className="space-y-4">
          <Input
            label="Division Name"
            error={divisionForm.formState.errors.name?.message}
            {...divisionForm.register("name")}
          />
          <Input
            label="Grade Range"
            placeholder="e.g. 1st–4th"
            error={divisionForm.formState.errors.ageRange?.message}
            {...divisionForm.register("ageRange")}
          />
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1.5">
              Description (optional)
            </label>
            <textarea
              {...divisionForm.register("description")}
              rows={2}
              className="block w-full rounded-lg border border-stone-300 text-sm px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center justify-between gap-3 pt-2">
            {editingDivision ? (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  icon={<ArchiveBoxIcon className="w-4 h-4" />}
                  onClick={handleArchiveDivision}
                  loading={archivingDivision}
                >
                  {editingDivision.archivedAt ? "Restore" : "Archive"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  icon={<TrashIcon className="w-4 h-4" />}
                  onClick={() => {
                    setDivisionToDelete({ id: editingDivision.id, name: editingDivision.name, fromCampMinder: editingDivision.campMinderDivisionId != null });
                    setDivisionModalOpen(false);
                    setEditingDivision(null);
                    setDeleteDivisionConfirm(true);
                  }}
                >
                  Delete
                </Button>
              </div>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setDivisionModalOpen(false);
                  setEditingDivision(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={savingDivision}>
                {editingDivision ? "Save Changes" : "Create"}
              </Button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Cabin Modal */}
      <Modal
        open={cabinModalOpen}
        onClose={() => {
          setCabinModalOpen(false);
          setEditingCabin(null);
        }}
        title={editingCabin ? "Edit Cabin" : "Create Cabin"}
      >
        <form onSubmit={cabinForm.handleSubmit(onSubmitCabin)} className="space-y-4">
          <Input
            label="Cabin Name"
            error={cabinForm.formState.errors.name?.message}
            {...cabinForm.register("name")}
          />
          <Select
            label="Division"
            placeholder="Select a division"
            options={[{ value: 0, label: "Unassigned" }, ...divisions.filter((d) => !d.archivedAt).map((d) => ({ value: d.id, label: d.name }))]}
            error={cabinForm.formState.errors.divisionId?.message}
            {...cabinForm.register("divisionId")}
          />
          <div className="flex items-center justify-between gap-3 pt-2">
            {editingCabin ? (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  icon={<ArchiveBoxIcon className="w-4 h-4" />}
                  onClick={handleArchiveCabin}
                  loading={archivingCabin}
                >
                  {editingCabin.archivedAt ? "Restore" : "Archive"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  icon={<TrashIcon className="w-4 h-4" />}
                  onClick={() => {
                    setCabinToDelete({ id: editingCabin.id, name: editingCabin.name, fromCampMinder: editingCabin.campMinderBunkId != null });
                    setCabinModalOpen(false);
                    setEditingCabin(null);
                    setDeleteCabinConfirm(true);
                  }}
                >
                  Delete
                </Button>
              </div>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setCabinModalOpen(false);
                  setEditingCabin(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={savingCabin}>
                {editingCabin ? "Save Changes" : "Create"}
              </Button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Delete Division Confirm */}
      <ConfirmDialog
        open={deleteDivisionConfirm}
        onClose={() => { setDeleteDivisionConfirm(false); setDivisionToDelete(null); }}
        onConfirm={handleDeleteDivision}
        title="Delete Division"
        message={
          divisionToDelete?.fromCampMinder
            ? `Are you sure you want to permanently delete "${divisionToDelete?.name}"? Cabins in this division will become unassigned. This division is synced from CampMinder and can be restored by running sync again.`
            : `Are you sure you want to permanently delete "${divisionToDelete?.name}"? This division was manually created and is not linked to CampMinder — it cannot be recovered by sync. Cabins in this division will become unassigned. This action cannot be undone.`
        }
        confirmLabel={divisionToDelete?.fromCampMinder ? "Delete Forever" : "Permanently Delete"}
        variant="danger"
        loading={deletingDivision}
      />

      {/* Delete Cabin Confirm */}
      <ConfirmDialog
        open={deleteCabinConfirm}
        onClose={() => { setDeleteCabinConfirm(false); setCabinToDelete(null); }}
        onConfirm={handleDeleteCabin}
        title="Delete Cabin"
        message={
          cabinToDelete?.fromCampMinder
            ? `Are you sure you want to permanently delete "${cabinToDelete?.name}"? Campers in this cabin will become unassigned. This cabin is synced from CampMinder and can be restored by running sync again.`
            : `Are you sure you want to permanently delete "${cabinToDelete?.name}"? This cabin was manually created and is not linked to CampMinder — it cannot be recovered by sync. Campers in this cabin will become unassigned. This action cannot be undone.`
        }
        confirmLabel={cabinToDelete?.fromCampMinder ? "Delete Forever" : "Permanently Delete"}
        variant="danger"
        loading={deletingCabin}
      />

      {/* Cabin Detail / Assignment Modal */}
      {selectedCabin && (
        <CabinDetailModal
          open={cabinDetailOpen}
          cabin={selectedCabin}
          cabinGroups={[]}
          year={CURRENT_YEAR}
          onClose={() => { setCabinDetailOpen(false); setSelectedCabin(null); }}
          onUpdated={() => fetchData()}
        />
      )}
    </div>
  );
}
